import { getEventTargetMouseOffset } from '@collector/shared-ui'

export type Coordinates = {
  x: number // int 1 - 100
  y: number // int 1 - 100
}

export const COORDINATE_COUNT = 100

export function validateCoordinate(coordinate: number): void {
  if (!Number.isInteger(coordinate)) {
    throw new Error(`coordinate ${coordinate} must be an integer.`)
  }

  if (coordinate < 1 || coordinate > 100) {
    throw new Error(
      `coordinate ${coordinate} must be between 1 and 100 inclusive.`,
    )
  }
}

function getNormalizedCoordinate(
  value: number,
  coordinateCount: number,
): number {
  if (value === 0) {
    return 1
  }

  const roundedValue = Math.ceil(value)

  if (roundedValue > coordinateCount) {
    return coordinateCount
  }

  return roundedValue
}

/**
 * Divide `dimensionLength` into equal parts and return coordinate - ordinal nr of part which contains given `mouseOffset`.
 */
export function getCoordinate(
  mouseOffset: number,
  dimensionLength: number,
  coordinateCount: number,
): number {
  // MouseEvent can capture negative offset (outside element bounds), treat it as 0
  const offset = Math.max(0, mouseOffset)

  // divide dimension into equal parts
  const partWidth = dimensionLength / coordinateCount

  return getNormalizedCoordinate(offset / partWidth, coordinateCount)
}

export function getMouseEventCoordinates(
  event: MouseEvent,
  targetElement: HTMLElement,
): Coordinates {
  const { eventOffsetX, eventOffsetY } = getEventTargetMouseOffset(
    event,
    targetElement,
  )

  const targetRectangle = targetElement.getBoundingClientRect()

  const x = getCoordinate(eventOffsetX, targetRectangle.width, COORDINATE_COUNT)
  const y = getCoordinate(
    eventOffsetY,
    targetRectangle.height,
    COORDINATE_COUNT,
  )

  return { x, y }
}

/**
 * Normalize coordinates for same team either on home or away side
 * If `teamSideChanged` is `true`, coordinates are flipped horizontally and vertically.
 */
export function getCoordinatesWithTeamSideChanged(
  coordinates: Coordinates,
  teamSideChanged: boolean,
): Coordinates {
  return teamSideChanged
    ? {
        x: COORDINATE_COUNT - coordinates.x + 1, // +1 because coordinates are 1-based
        y: COORDINATE_COUNT - coordinates.y + 1, // +1 because coordinates are 1-based
      }
    : coordinates
}
